import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CFDI_USAGE_OPTIONS, FISCAL_REGIME_OPTIONS, DEFAULT_CFDI_USAGE, DEFAULT_FISCAL_REGIME } from '../constants/cfdiOptions';
import { createCustomer } from '../utils/customerService';

// RFC validation function for Mexican tax ID
const validateRFC = (rfc) => {
    // Remove spaces and convert to uppercase
    const cleanRFC = rfc.replace(/\s/g, '').toUpperCase();
    
    // RFC format: 4 letters + 6 numbers + 3 characters (AAAA123456XXX)
    // Or 3 letters + 6 numbers + 3 characters for individuals (AAA123456XXX)
    const rfcRegex = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/;
    
    if (!rfcRegex.test(cleanRFC)) {
        return { valid: false, error: 'Formato de RFC inválido' };
    }
    
    // Additional validation: check length
    if (cleanRFC.length !== 12 && cleanRFC.length !== 13) {
        return { valid: false, error: 'El RFC debe tener 12 o 13 caracteres' };
    }
    
    return { valid: true, rfc: cleanRFC };
};

// Mock function to validate RFC with SAT
// In real implementation, this would call SAT API or validation service
const mockValidateRFCWithSAT = async (rfc) => {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Mock validation - in real implementation this would call SAT API
    // For testing, we'll accept most RFCs that pass format validation
    const testInvalidRFCs = ['XAXX010101000', 'XEXX010101000']; // Mock invalid RFCs
    
    if (testInvalidRFCs.includes(rfc)) {
        return { valid: false, error: 'RFC no válido en el SAT' };
    }
    
    return { valid: true };
};

// Postal code validation for Mexico
const validatePostalCode = (cp) => {
    const cpRegex = /^\d{5}$/;
    if (!cpRegex.test(cp)) {
        return { valid: false, error: 'El código postal debe tener 5 dígitos' };
    }
    return { valid: true };
};

const CustomerRegistration = ({ sale, onCustomerRegistered, onError, onGoBack }) => {
    const [formData, setFormData] = useState({
        rfc: '',
        razonSocial: '',
        email: '',
        telefono: '',
        codigoPostal: '',
        usoCfdi: DEFAULT_CFDI_USAGE,
        regimenFiscal: DEFAULT_FISCAL_REGIME
    });
    
    const [errors, setErrors] = useState({});
    const [isValidatingRFC, setIsValidatingRFC] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [rfcValidated, setRFCValidated] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleRFCValidation = async () => {
        const rfcValidation = validateRFC(formData.rfc);
        
        if (!rfcValidation.valid) {
            setErrors(prev => ({ ...prev, rfc: rfcValidation.error }));
            return;
        }

        setIsValidatingRFC(true);
        setRFCValidated(false);
        
        try {
            const satValidation = await mockValidateRFCWithSAT(rfcValidation.rfc);
            
            if (!satValidation.valid) {
                setErrors(prev => ({ ...prev, rfc: satValidation.error }));
            } else {
                setRFCValidated(true);
                setFormData(prev => ({ ...prev, rfc: rfcValidation.rfc }));
                setErrors(prev => ({ ...prev, rfc: '' }));
            }
        } catch (error) {
            console.error('Error validating RFC:', error);
            setErrors(prev => ({ ...prev, rfc: 'Error al validar RFC con SAT. Intente nuevamente.' }));
        } finally {
            setIsValidatingRFC(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Required fields validation
        if (!formData.rfc.trim()) {
            newErrors.rfc = 'RFC es obligatorio';
        } else if (!rfcValidated) {
            newErrors.rfc = 'Debe validar el RFC';
        }

        if (!formData.razonSocial.trim()) {
            newErrors.razonSocial = 'Razón social es obligatoria';
        }

        if (!formData.codigoPostal.trim()) {
            newErrors.codigoPostal = 'Código postal es obligatorio';
        } else {
            const cpValidation = validatePostalCode(formData.codigoPostal);
            if (!cpValidation.valid) {
                newErrors.codigoPostal = cpValidation.error;
            }
        }

        if (!formData.usoCfdi.trim()) {
            newErrors.usoCfdi = 'Uso de CFDI es obligatorio';
        }

        if (!formData.regimenFiscal.trim()) {
            newErrors.regimenFiscal = 'Régimen fiscal es obligatorio';
        }

        // Email validation (optional)
        if (formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = 'Email inválido';
            }
        }

        // Phone validation (optional)
        if (formData.telefono.trim()) {
            const phoneRegex = /^\d{10}$/;
            if (!phoneRegex.test(formData.telefono.replace(/\D/g, ''))) {
                newErrors.telefono = 'Teléfono debe tener 10 dígitos';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Call API to save customer with sale ID and sucursal ID
            const customerFormData = {
                ...formData,
                // Add sale ID if available
                ventaId: sale?.id || null,
                // Add sucursal ID if available
                sucursalId: sale?.sucursalId || null
            };
            
            console.log('Enviando datos de cliente con ID de venta:', sale?.id, 'y ID de sucursal:', sale?.sucursalId);
            const result = await createCustomer(customerFormData);
            
            if (!result.success) {
                // Display error similar to PAC errors in invoice generation
                onError(result.error);
                return;
            }
            
            // If successful (201), continue with the flow
            // Check if the API returned cliente_id in the response format mentioned
            if (result.cliente && result.cliente.cliente_id) {
                // Use the cliente_id from the API response
                const customerData = {
                    ...result.data,
                    id: result.cliente.cliente_id
                };
                console.log('Cliente registrado exitosamente con ID:', result.cliente.cliente_id);
                console.log('Cliente data completo:', JSON.stringify(customerData, null, 2));
                
                onCustomerRegistered(customerData);
            } else {
                // Fall back to the original behavior if the response format is different
                const customerData = result.data;
                console.log('Cliente registrado exitosamente:', JSON.stringify(customerData, null, 2));
                
                onCustomerRegistered(customerData);
            }
            
        } catch (error) {
            console.error('Error registering customer:', error);
            onError('Error al registrar los datos del cliente. Por favor intente nuevamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="customer-registration">
            <div className="registration-header">
                <h2>Datos Fiscales</h2>
                <p>Ingrese sus datos fiscales para generar la factura</p>
            </div>

            <form onSubmit={handleSubmit} className="registration-form">
                {/* RFC Section */}
                <div className="form-section">
                    <h3>Información Fiscal</h3>
                    
                    <div className="input-group">
                        <label htmlFor="rfc">RFC *</label>
                        <div className="rfc-input">
                            <input
                                id="rfc"
                                type="text"
                                value={formData.rfc}
                                onChange={(e) => handleInputChange('rfc', e.target.value.toUpperCase())}
                                placeholder="AAAA123456XXX"
                                maxLength="13"
                                disabled={isValidatingRFC || rfcValidated}
                                className={errors.rfc ? 'input-error' : rfcValidated ? 'input-success' : ''}
                            />
                            <button
                                type="button"
                                onClick={handleRFCValidation}
                                disabled={!formData.rfc.trim() || isValidatingRFC || rfcValidated}
                                className="validate-rfc-btn"
                            >
                                {isValidatingRFC ? 'Validando...' : rfcValidated ? '✓ Válido' : 'Validar'}
                            </button>
                        </div>
                        {errors.rfc && <div className="field-error">{errors.rfc}</div>}
                    </div>

                    <div className="input-group">
                        <label htmlFor="razonSocial">Razón Social *</label>
                        <input
                            id="razonSocial"
                            type="text"
                            value={formData.razonSocial}
                            onChange={(e) => handleInputChange('razonSocial', e.target.value)}
                            placeholder="Nombre o razón social completa"
                            className={errors.razonSocial ? 'input-error' : ''}
                        />
                        {errors.razonSocial && <div className="field-error">{errors.razonSocial}</div>}
                    </div>
                </div>

                {/* Contact Information */}
                <div className="form-section">
                    <h3>Información de Contacto</h3>
                    
                    <div className="input-group">
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="ejemplo@correo.com (opcional)"
                            className={errors.email ? 'input-error' : ''}
                        />
                        {errors.email && <div className="field-error">{errors.email}</div>}
                        <small>Si proporciona su email, podrá recibir la factura por correo</small>
                    </div>

                    <div className="input-group">
                        <label htmlFor="telefono">Teléfono</label>
                        <input
                            id="telefono"
                            type="tel"
                            value={formData.telefono}
                            onChange={(e) => handleInputChange('telefono', e.target.value)}
                            placeholder="10 dígitos (opcional)"
                            maxLength="10"
                            className={errors.telefono ? 'input-error' : ''}
                        />
                        {errors.telefono && <div className="field-error">{errors.telefono}</div>}
                    </div>
                </div>

                {/* Fiscal Information */}
                <div className="form-section">
                    <h3>Información Fiscal</h3>
                    
                    <div className="input-group">
                        <label htmlFor="codigoPostal">Código Postal *</label>
                        <input
                            id="codigoPostal"
                            type="text"
                            value={formData.codigoPostal}
                            onChange={(e) => handleInputChange('codigoPostal', e.target.value)}
                            placeholder="12345"
                            maxLength="5"
                            className={errors.codigoPostal ? 'input-error' : ''}
                        />
                        {errors.codigoPostal && <div className="field-error">{errors.codigoPostal}</div>}
                    </div>

                    <div className="input-group">
                        <label htmlFor="usoCfdi">Uso de CFDI *</label>
                        <select
                            id="usoCfdi"
                            value={formData.usoCfdi}
                            onChange={(e) => handleInputChange('usoCfdi', e.target.value)}
                            className={errors.usoCfdi ? 'input-error' : ''}
                        >
                            {CFDI_USAGE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors.usoCfdi && <div className="field-error">{errors.usoCfdi}</div>}
                        <small>Seleccione el uso que le dará a esta factura</small>
                    </div>

                    <div className="input-group">
                        <label htmlFor="regimenFiscal">Régimen Fiscal *</label>
                        <select
                            id="regimenFiscal"
                            value={formData.regimenFiscal}
                            onChange={(e) => handleInputChange('regimenFiscal', e.target.value)}
                            className={errors.regimenFiscal ? 'input-error' : ''}
                        >
                            {FISCAL_REGIME_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errors.regimenFiscal && <div className="field-error">{errors.regimenFiscal}</div>}
                        <small>Seleccione su régimen fiscal actual</small>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={onGoBack}
                        className="btn-secondary"
                        disabled={isSubmitting}
                    >
                        ← Regresar
                    </button>
                    
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={!rfcValidated || isSubmitting}
                    >
                        {isSubmitting ? 'Guardando...' : 'Continuar →'}
                    </button>
                </div>
            </form>
        </div>
    );
};

CustomerRegistration.propTypes = {
    sale: PropTypes.shape({
        id: PropTypes.number,
        sucursalId: PropTypes.number,
        sucursalName: PropTypes.string,
        fechaVenta: PropTypes.string,
        total: PropTypes.number,
        productos: PropTypes.array
    }),
    onCustomerRegistered: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onGoBack: PropTypes.func.isRequired
};

export default CustomerRegistration;